// createUser — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-user' })` op (DSE2 §3.F1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice F1 — Logins / Users / Roles UI.
//
// Per-engine surface (per DSE2 dialect.user):
//   - mssql:    `CREATE USER … [FOR LOGIN | WITHOUT LOGIN] [WITH DEFAULT_SCHEMA]`
//   - mysql:    `CREATE USER 'name'@'host' [IDENTIFIED BY|WITH] [REQUIRE] [PASSWORD EXPIRE]`
//   - oracle:   `CREATE USER … {IDENTIFIED BY|EXTERNALLY} [DEFAULT TABLESPACE] [TEMPORARY TABLESPACE] [QUOTA] [PROFILE] [ACCOUNT LOCK|UNLOCK]`
//   - postgres: rejected — collapses users into roles (use create-role + pgsqlLogin)
//   - sqlite:   rejected — no auth model.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, UserDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreateUserArgs {
  readonly user: UserDef;
}

export function buildCreateUserEdit(args: CreateUserArgs): DdlEdit {
  return { op: 'create-user', user: args.user };
}

export async function commitCreateUser(
  ctx: VqeDdlEditContext,
  args: CreateUserArgs,
): Promise<void> {
  const edit = buildCreateUserEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const CREATE_USER_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'mysql', 'oracle',
] as const);

export function isCreateUserSupported(engine: SqlEngine): boolean {
  return CREATE_USER_SUPPORTED.has(engine);
}

export function getCreateUserUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return 'SQLite has no auth model — connection-level access is OS / file-permission driven.';
  }
  if (engine === 'postgres') {
    return 'PostgreSQL collapses users into roles. Use "Role" with the LOGIN attribute set.';
  }
  return '';
}

export function validateCreateUserForEngine(
  engine: SqlEngine,
  args: CreateUserArgs,
): string | null {
  if (!isCreateUserSupported(engine)) {
    return getCreateUserUnsupportedTooltip(engine);
  }
  const u = args.user;
  if (u.name.trim().length === 0) {
    return 'User name is required.';
  }
  if (engine === 'mssql') {
    if (u.mssqlForLogin && u.mssqlWithoutLogin) {
      return 'Pick FOR LOGIN OR WITHOUT LOGIN, not both.';
    }
  }
  if (engine === 'mysql') {
    if (u.mysqlIdentifiedBy && u.mysqlIdentifiedWith) {
      return 'Pick IDENTIFIED BY (password) OR IDENTIFIED WITH (auth plugin), not both.';
    }
  }
  if (engine === 'oracle') {
    const hasPassword = !!u.oracleIdentifiedBy && u.oracleIdentifiedBy.length > 0;
    const hasExternal = u.oracleIdentifiedExternally === true;
    if (hasPassword && hasExternal) {
      return 'Pick IDENTIFIED BY OR IDENTIFIED EXTERNALLY, not both.';
    }
    if (!hasPassword && !hasExternal) {
      return 'Oracle users require IDENTIFIED BY <password> or IDENTIFIED EXTERNALLY.';
    }
  }
  if (engine !== 'mssql' && (u.mssqlForLogin || u.mssqlWithoutLogin || u.mssqlDefaultSchema)) {
    return `${engine} does not accept mssql-only user options.`;
  }
  if (engine !== 'mysql' && (u.mysqlIdentifiedBy || u.mysqlIdentifiedWith
      || u.mysqlRequireText || u.mysqlPasswordExpireText)) {
    return `${engine} does not accept mysql-only user options.`;
  }
  if (engine !== 'oracle' && (u.oracleIdentifiedBy || u.oracleIdentifiedExternally
      || u.oracleDefaultTablespace || u.oracleTemporaryTablespace
      || u.oracleQuotaText || u.oracleProfile || u.oracleAccountLocked !== undefined)) {
    return `${engine} does not accept oracle-only user options.`;
  }
  return null;
}
